import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import HomeScreen from './screens/HomeScreen';
import ResultsScreen from './screens/ResultsScreen';
import Loader from './components/Loader';
import Message from './components/Message';
import { loadModels } from './utils/faceDetectionUtils';
import './index.css';

function App() {
  // State to manage analysis results
  const [analysisResults, setAnalysisResults] = useState(null);
  // State to manage loading status
  const [loading, setLoading] = useState(false);
  // State to manage error messages
  const [error, setError] = useState(null);
  // State to track if models are loaded
  const [modelsLoaded, setModelsLoaded] = useState(false);

  // Load TensorFlow.js models on app start
  useEffect(() => {
    const initModels = async () => {
      try {
        await loadModels();
        setModelsLoaded(true);
        console.log('Face detection models loaded successfully');
      } catch (error) {
        console.error('Error loading models:', error);
        setError('Failed to load face detection models. Please refresh the page and try again.');
      }
    };
    
    initModels();
  }, []);

  // Function to handle successful analysis
  const handleAnalysisComplete = (results) => {
    setAnalysisResults(results);
    setLoading(false);
    setError(null);
  };

  // Function to handle analysis errors
  const handleAnalysisError = (errorMessage) => {
    setError(errorMessage);
    setLoading(false);
  };

  // Function to reset the application state
  const resetAnalysis = () => {
    setAnalysisResults(null);
    setError(null);
  };

  return (
    <Router>
      <div className="flex flex-col min-h-screen bg-gray-50">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-8">
          {/* Show loading spinner when processing */}
          {loading && <Loader text="Analyzing face shape..." />}
          
          {/* Show error message if there's an error */}
          {error && <Message variant="error" message={error} onClose={() => setError(null)} />}
          
          {!modelsLoaded && !loading && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    Loading face detection models... Please wait before analyzing images.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <Routes>
            {/* Home/Upload screen */}
            <Route 
              path="/" 
              element={
                <HomeScreen 
                  onAnalysisStart={() => setLoading(true)} 
                  onAnalysisComplete={handleAnalysisComplete}
                  onAnalysisError={handleAnalysisError}
                />
              } 
            />
            
            {/* Results screen - redirects to home if no analysis results */}
            <Route 
              path="/results" 
              element={
                analysisResults ? (
                  <ResultsScreen 
                    results={analysisResults} 
                    onNewAnalysis={resetAnalysis}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              } 
            />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;